import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Verwaltung → Konfiguration: wohin die hochgeladenen Dateien eines Betriebs gehen.
 *
 * Ohne Eintrag liegen sie im Ablageverzeichnis des Servers. Wer einen eigenen
 * Objektspeicher hat, trägt ihn hier ein — ab dann gehen alle neuen Uploads dorthin.
 * Bereits abgelegte Dateien bleiben, wo sie sind; sie tragen ihren Speicherort selbst.
 */

interface KonfigurationAntwort {
  mandantId: number
  mandantName: string | null
  aktiv: boolean
  endpunkt: string | null
  region: string | null
  bucket: string | null
  praefix: string | null
  zugriffsschluessel: string | null
  geheimschluesselHinterlegt: boolean
  pfadstil: boolean
  geaendertAm: string | null
  lokalesVerzeichnis: string
  verzeichnisParameter: string | null
  aktuelleArt: string
  maxGroesseMb: number
}

interface VerzeichnisAntwort {
  verzeichnisParameter: string | null
  wirksam: string
  pruefungErfolgreich: boolean
  pruefung: string
}

interface PruefAntwort {
  erfolgreich: boolean
  art: string
  meldung: string
}

interface UebersichtZeile {
  mandantId: number
  mandantName: string
  kuerzel: string
  s3Aktiv: boolean
  bucket: string | null
  endpunkt: string | null
}

export function Ablagekonfiguration() {
  const { benutzer, hatRolle } = useAuth()
  const istPlattformAdmin = hatRolle('PLATTFORM_ADMIN')

  const [mandantId, setMandantId] = useState<number | null>(null)
  const [daten, setDaten] = useState<KonfigurationAntwort | null>(null)
  const [geheim, setGeheim] = useState('')
  const [verzeichnis, setVerzeichnis] = useState('')
  const [uebersicht, setUebersicht] = useState<UebersichtZeile[]>([])
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const laden = useCallback(async () => {
    try {
      const abfrage = mandantId != null ? `?mandantId=${mandantId}` : ''
      const antwort = await api.get<KonfigurationAntwort>(`/api/ablage/konfiguration${abfrage}`)
      setDaten(antwort)
      setVerzeichnis(antwort.verzeichnisParameter ?? '')
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    }
  }, [mandantId])

  useEffect(() => {
    void laden()
  }, [laden])

  useEffect(() => {
    if (!istPlattformAdmin) return
    api
      .get<UebersichtZeile[]>('/api/ablage/uebersicht')
      .then(setUebersicht)
      .catch(() => setUebersicht([]))
  }, [istPlattformAdmin])

  async function speichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (!daten) return
    setLaeuft(true)
    setMeldung(null)
    try {
      const abfrage = mandantId != null ? `?mandantId=${mandantId}` : ''
      // Ein leeres Feld bedeutet „unverändert" — der geheime Schlüssel wird nie angezeigt.
      const antwort = await api.put<KonfigurationAntwort>(
        `/api/ablage/konfiguration${abfrage}`,
        {
          aktiv: daten.aktiv,
          endpunkt: daten.endpunkt,
          region: daten.region,
          bucket: daten.bucket,
          praefix: daten.praefix,
          zugriffsschluessel: daten.zugriffsschluessel,
          geheimschluessel: geheim || null,
          pfadstil: daten.pfadstil,
        },
      )
      setDaten(antwort)
      setGeheim('')
      setMeldung({ art: 'erfolg', text: 'Einstellungen gespeichert' })
    } catch (ex) {
      setMeldung({
        art: 'fehler',
        text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen',
      })
    } finally {
      setLaeuft(false)
    }
  }

  async function verzeichnisSpeichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    setLaeuft(true)
    setMeldung(null)
    try {
      const e = await api.put<VerzeichnisAntwort>('/api/ablage/verzeichnis', { verzeichnis })
      setMeldung({
        art: e.pruefungErfolgreich ? 'erfolg' : 'fehler',
        text: e.pruefungErfolgreich
          ? `Gespeichert. ${e.pruefung}`
          : `Gespeichert, aber: ${e.pruefung}`,
      })
      await laden()
    } catch (ex) {
      setMeldung({
        art: 'fehler',
        text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen',
      })
    } finally {
      setLaeuft(false)
    }
  }

  async function pruefen() {
    setLaeuft(true)
    setMeldung(null)
    try {
      const abfrage = mandantId != null ? `?mandantId=${mandantId}` : ''
      const e = await api.post<PruefAntwort>(`/api/ablage/pruefen${abfrage}`)
      setMeldung({ art: e.erfolgreich ? 'erfolg' : 'fehler', text: e.meldung })
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Prüfung fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  if (!daten) return <div className="laden">Wird geladen …</div>

  const s3Laeuft = daten.aktuelleArt === 'S3'

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Konfiguration</h1>
          <p>
            Wohin hochgeladene Kursblätter, Fahrpläne und Anhänge gehen. Größe je Datei:
            höchstens {daten.maxGroesseMb} MB.
          </p>
        </div>
        {istPlattformAdmin && uebersicht.length > 0 && (
          <div className="feld" style={{ minWidth: 240 }}>
            <label htmlFor="mandant">Betrieb</label>
            <select
              id="mandant"
              value={mandantId ?? benutzer?.mandantId ?? ''}
              onChange={(e) => setMandantId(Number(e.target.value))}
            >
              {uebersicht.map((m) => (
                <option key={m.mandantId} value={m.mandantId}>
                  {m.mandantName} ({m.kuerzel})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      <div className={`meldung ${s3Laeuft ? 'erfolg' : 'hinweis'}`}>
        {s3Laeuft ? (
          <>
            Neue Dateien gehen in den S3-Speicher <strong>{daten.bucket}</strong>. Bereits
            abgelegte Dateien bleiben, wo sie sind — jede trägt ihren Speicherort selbst.
          </>
        ) : (
          <>
            Neue Dateien gehen in das Verzeichnis <strong>{daten.lokalesVerzeichnis}</strong> auf
            dem Server, darunter nach <code>&lt;Betrieb&gt;/data/&lt;Jahr&gt;/&lt;Monat&gt;</code>.
            Das Verzeichnis muss für den Tomcat beschreibbar sein und gehört in die
            Datensicherung.
          </>
        )}
      </div>

      {/* Der Pfad gilt plattformweit und steht deshalb nur dem Plattform-Administrator
          offen. Er liegt im Systemparameter ablage.verzeichnis - derselbe Wert wie unter
          Konfiguration -> Parameter, hier nur an der Stelle, an der man ihn sucht. */}
      {istPlattformAdmin && (
        <div className="karte abschnitt">
          <h2>Verzeichnis der lokalen Ablage</h2>
          <p style={{ color: 'var(--grau-500)', marginTop: 0 }}>
            Gilt für alle Betriebe ohne eigenen S3-Speicher. Im Produktivbetrieb der Pfad, der
            auf allen Tomcats erreichbar ist — etwa <code>/srv/data/vlzhub</code>. Leer
            bedeutet: Es gilt die Anwendungseinstellung.
          </p>

          <form onSubmit={verzeichnisSpeichern}>
            <div className="feld">
              <label htmlFor="verzeichnis">Pfad</label>
              <input
                id="verzeichnis"
                type="text"
                placeholder="/srv/data/vlzhub"
                value={verzeichnis}
                onChange={(e) => setVerzeichnis(e.target.value)}
              />
            </div>
            <p style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
              Wirksam ist derzeit <code>{daten.lokalesVerzeichnis}</code>
              {!daten.verzeichnisParameter && ' (aus der Anwendungskonfiguration)'}. Beim
              Speichern wird sofort geprüft, ob sich dort schreiben lässt — ein vertippter Pfad
              oder ein fehlendes Schreibrecht fällt damit hier auf und nicht erst beim nächsten
              Upload.
            </p>
            <button className="knopf-primaer" type="submit" disabled={laeuft}>
              {laeuft ? 'Wird gespeichert …' : 'Verzeichnis speichern und prüfen'}
            </button>
          </form>
        </div>
      )}

      <div className="karte abschnitt">
        <h2>Eigener S3-Speicher</h2>
        <p style={{ color: 'var(--grau-500)', marginTop: 0 }}>
          Angesprochen wird jeder S3-kompatible Speicher — AWS, MinIO im eigenen Haus, Hetzner,
          Wasabi, IONOS. Ohne Eintrag bleibt es bei der Ablage auf dem Server.
        </p>

        <form onSubmit={speichern}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 14,
            }}
          >
            <div className="feld">
              <label htmlFor="bucket">Bucket</label>
              <input
                id="bucket"
                type="text"
                placeholder="vlzhub-betrieb"
                value={daten.bucket ?? ''}
                onChange={(e) => setDaten({ ...daten, bucket: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="endpunkt">Endpunkt</label>
              <input
                id="endpunkt"
                type="text"
                placeholder="leer = AWS, sonst z. B. https://minio.betrieb.de"
                value={daten.endpunkt ?? ''}
                onChange={(e) => setDaten({ ...daten, endpunkt: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="region">Region</label>
              <input
                id="region"
                type="text"
                placeholder="eu-central-1"
                value={daten.region ?? ''}
                onChange={(e) => setDaten({ ...daten, region: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="praefix">Namensvorsatz im Bucket</label>
              <input
                id="praefix"
                type="text"
                placeholder="optional, z. B. vlzhub/"
                value={daten.praefix ?? ''}
                onChange={(e) => setDaten({ ...daten, praefix: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="zugriff">Zugriffsschlüssel</label>
              <input
                id="zugriff"
                type="text"
                autoComplete="off"
                value={daten.zugriffsschluessel ?? ''}
                onChange={(e) => setDaten({ ...daten, zugriffsschluessel: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="geheim">
                Geheimer Schlüssel{' '}
                {daten.geheimschluesselHinterlegt && '(hinterlegt — leer lassen = unverändert)'}
              </label>
              <input
                id="geheim"
                type="password"
                autoComplete="new-password"
                placeholder={daten.geheimschluesselHinterlegt ? '••••••••' : ''}
                value={geheim}
                onChange={(e) => setGeheim(e.target.value)}
              />
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={daten.pfadstil}
                onChange={(e) => setDaten({ ...daten, pfadstil: e.target.checked })}
              />
              <span>
                Pfadstil verwenden
                <div style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
                  <code>https://host/bucket/objekt</code> statt{' '}
                  <code>https://bucket.host/objekt</code>. MinIO und die meisten selbst
                  betriebenen Speicher brauchen das; bei AWS bleibt es aus.
                </div>
              </span>
            </label>

            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={daten.aktiv}
                onChange={(e) => setDaten({ ...daten, aktiv: e.target.checked })}
              />
              <span>
                S3 verwenden
                <div style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
                  Erst prüfen, dann einschalten. Ist der Zugang falsch, scheitert danach jeder
                  Upload.
                </div>
              </span>
            </label>
          </div>

          <div className="werkzeugleiste" style={{ marginTop: 16, gap: 10 }}>
            <button className="knopf-primaer" type="submit" disabled={laeuft}>
              {laeuft ? 'Wird gespeichert …' : 'Speichern'}
            </button>
            <button
              className="knopf-schlicht"
              type="button"
              disabled={laeuft}
              onClick={() => void pruefen()}
            >
              Verbindung prüfen
            </button>
          </div>
          <p style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
            Geprüft wird der gespeicherte Stand, nicht die Eingabefelder — erst speichern, dann
            prüfen. Der Test legt eine Probedatei an und entfernt sie wieder; ein reiner
            Lesezugriff genügt für den Betrieb nicht.
          </p>
        </form>
      </div>

      {istPlattformAdmin && uebersicht.length > 0 && (
        <div className="abschnitt">
          <h2>Ablage aller Betriebe</h2>
          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th>Betrieb</th>
                  <th>Ablage</th>
                  <th>Bucket</th>
                  <th>Endpunkt</th>
                </tr>
              </thead>
              <tbody>
                {uebersicht.map((m) => (
                  <tr key={m.mandantId}>
                    <td>
                      <strong>{m.mandantName}</strong>{' '}
                      <span style={{ color: 'var(--grau-500)' }}>({m.kuerzel})</span>
                    </td>
                    <td>
                      <span className={`etikett ${m.s3Aktiv ? 'gruen' : 'grau'}`}>
                        {m.s3Aktiv ? 'S3' : 'Server'}
                      </span>
                    </td>
                    <td>{m.bucket ?? '–'}</td>
                    <td>{m.endpunkt ?? (m.s3Aktiv ? 'AWS' : '–')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}
